import { useEffect, useRef, useState } from 'react'
import { api } from '../../shared/api/http'
import './cameraPtz.css'

type PtzState = { configured: boolean; online: boolean; capabilities: {
  supported: boolean; pan: boolean; tilt: boolean; zoom: boolean
  presetsAvailable: boolean; presets: { id: number; name: string }[]
} | null }

export function CameraPtz({ companyId, cameraId }: { companyId: string; cameraId: string }) {
  const [state, setState] = useState<PtzState | null>(null)
  const [open, setOpen] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [speed, setSpeed] = useState(2)
  const [preset, setPreset] = useState('')
  const pending = useRef(false)
  const mounted = useRef(true)
  const path = `/companies/${companyId}/cameras/${cameraId}/ptz`
  useEffect(() => {
    mounted.current = true
    let alive = true
    let timer: ReturnType<typeof setTimeout>
    async function refresh() {
      try {
        const value = await api<PtzState>(path)
        if (alive) setState(value)
      } catch { if (alive) setState(value => value ? { ...value, online: false } : null) }
      if (alive) timer = setTimeout(refresh, 5000)
    }
    void refresh()
    const stop = () => { if (pending.current) void api(`${path}/commands`, { method: 'POST', body: JSON.stringify({ action: 'stop' }) }).catch(() => {}) }
    const visibility = () => { if (document.hidden) stop() }
    window.addEventListener('blur', stop)
    document.addEventListener('visibilitychange', visibility)
    return () => {
      alive = false; mounted.current = false; clearTimeout(timer); stop()
      window.removeEventListener('blur', stop)
      document.removeEventListener('visibilitychange', visibility)
    }
  }, [path])

  async function command(action: string, presetId?: number) {
    if (pending.current && action !== 'stop') return
    pending.current = true; setBusy(true); setError('')
    try { await api(`${path}/commands`, { method: 'POST', body: JSON.stringify({ action, speed, presetId }) }) }
    catch { if (mounted.current) setError('Comando não confirmado. Confira a imagem antes de tentar novamente.') }
    finally { pending.current = false; if (mounted.current) setBusy(false) }
  }
  const caps = state?.capabilities
  if (!caps?.supported) return null
  const disabled = !state?.online || busy
  return <aside className="camera-ptz" aria-label="Controle da câmera" onDoubleClick={event => event.stopPropagation()}>
    <button className="ptz-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>✥ Controle da câmera {open ? '−' : '+'}</button>
    {open && <div className="ptz-content">
      {!state?.online && <p role="status">Controle indisponível. Verifique a conexão do Raspberry.</p>}
      <div className="ptz-directions">
        <button style={{ gridColumn: 2 }} disabled={disabled || !caps.tilt} aria-label="Mover para cima" onClick={() => void command('up')}>↑</button>
        <button style={{ gridColumn: 1 }} disabled={disabled || !caps.pan} aria-label="Mover para esquerda" onClick={() => void command('left')}>←</button>
        <button disabled={!state?.online} aria-label="Parar movimento" onClick={() => void command('stop')}>■</button>
        <button disabled={disabled || !caps.pan} aria-label="Mover para direita" onClick={() => void command('right')}>→</button>
        <button style={{ gridColumn: 2 }} disabled={disabled || !caps.tilt} aria-label="Mover para baixo" onClick={() => void command('down')}>↓</button>
      </div>
      <small>Toque para mover um pouco.</small>
      {caps.zoom && <div className="ptz-zoom"><button disabled={disabled} aria-label="Diminuir zoom óptico" onClick={() => void command('zoom-out')}>−</button><span>Zoom óptico</span><button disabled={disabled} aria-label="Aumentar zoom óptico" onClick={() => void command('zoom-in')}>+</button></div>}
      <label>Velocidade <select value={speed} disabled={busy} onChange={event => setSpeed(Number(event.target.value))}>{[1, 2, 3, 4, 5, 6, 7, 8].map(value => <option key={value}>{value}</option>)}</select></label>
      {caps.presets.length > 0 ? <div className="ptz-presets"><label>Posições salvas<select value={preset} onChange={event => setPreset(event.target.value)}><option value="">Escolha um preset</option>{caps.presets.map(item => <option key={item.id} value={item.id}>{item.id} · {item.name}</option>)}</select></label><button disabled={disabled || !preset || !caps.presets.some(item => String(item.id) === preset)} onClick={() => void command('preset', Number(preset))}>Ir para posição</button></div> : <small>Lista de presets não retornada pela câmera.</small>}
      {error && <p role="alert">{error}</p>}
    </div>}
  </aside>
}

export function PtzBridgeSetup({ companyId, clientId }: { companyId: string; clientId: string }) {
  const [open, setOpen] = useState(false), [id, setId] = useState(''), [busy, setBusy] = useState(false), [message, setMessage] = useState('')
  async function save() {
    setBusy(true); setMessage('')
    try {
      await api(`/companies/${companyId}/clients/${clientId}/ptz-bridges`, { method: 'POST', body: JSON.stringify({ bridgeId: id.trim() }) })
      setMessage('Raspberry vinculado. Os controles aparecerão nas câmeras PTZ quando ele conectar.'); setId('')
    } catch { setMessage('Não foi possível vincular. Confira o código, a validade de 1 hora e sua permissão neste cliente.') }
    finally { setBusy(false) }
  }
  return <>
    <button className="fullscreen-button" onClick={() => setOpen(true)}>Vincular PTZ</button>
    {open && <div className="ptz-setup-shade"><section role="dialog" aria-modal="true" aria-label="Vincular controle PTZ" className="ptz-setup">
      <h2>Vincular controle PTZ</h2><p>Informe o código público exibido pelo Raspberry deste cliente. Não informe a senha da câmera.</p>
      <label>Código de vinculação<input autoFocus value={id} onChange={event => setId(event.target.value)} placeholder="Código público do Raspberry" /></label>
      {message && <p role="status">{message}</p>}
      <div><button disabled={busy} onClick={() => setOpen(false)}>Fechar</button><button disabled={busy || !/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id.trim())} onClick={() => void save()}>{busy ? 'Vinculando…' : 'Vincular ao cliente selecionado'}</button></div>
    </section></div>}
  </>
}
